/**
 * The guidance outcome bus (WC-03): how a workflow coach learns that the
 * PERSON's real action went through, without the engine ever touching the
 * code that performs it.
 *
 * ── How it works ────────────────────────────────────────────────────────────
 * A feature declares, on the one mutation that IS a business step, which
 * outcome it is: `useMutation({ …, meta: { guidanceOutcome: 'request.submitted' } })`.
 * Nothing about the mutation changes. `GuidanceRoot` already listens to the
 * mutation cache (to stop a guide when the session ends); it forwards each
 * state change of a tagged mutation to the guide store as
 * `{ outcome, phase, variant?, serial }`.
 *
 * ── What crosses the bus, and what never does ──────────────────────────────
 * - the outcome ID, from this closed list (a typo is not an outcome);
 * - the phase: pending, success or error;
 * - at most ONE variant, read from ONE allowlisted field of the mutation's
 *   variables, and only when its value is in that field's allowlist (the
 *   approval decision) — never a free value;
 * - a serial, so a coach knows the event came after its step began.
 *
 * Never a response body, a record ID, a name, an amount or an error message.
 *
 * Pure: no import of TanStack, the API client or a feature module. The coach
 * engine reads only what the store hands it.
 */
export const GUIDANCE_OUTCOMES = [
  'cart.line-added',
  'request.submitted',
  'approval.decided',
  'rfq.created',
  'rfq.opened',
  'rfq.awarded',
  'rfq.closed',
  'po.generated',
  'po.issued',
  'po.sent',
  'po.invoice-captured',
  'receipt.recorded',
  'receipt.closed-short',
  'vendor.order.acknowledged',
  'vendor.order.answered',
  'vendor.order.dispatched',
  'vendor.quote.document-uploaded',
  'vendor.quote.submitted',
  'platform.organisation.provisioned',
] as const;

export type GuidanceOutcomeId = (typeof GUIDANCE_OUTCOMES)[number];

export type OutcomePhase = 'pending' | 'success' | 'error';

export interface GuidanceOutcomeEvent {
  outcome: GuidanceOutcomeId;
  phase: OutcomePhase;
  /** From the outcome's allowlisted variables field only (`OUTCOME_VARIANTS`). */
  variant?: string;
  /** Raised by the store for every event: later events have higher serials. */
  serial: number;
}

/**
 * The only variants there are: per outcome, the one field of the mutation's
 * variables it may come from and the values it may take. A value outside the
 * list is dropped, never passed on. Extended only with a verified field (the
 * feature's own variables type), when a coach needs it.
 */
export const OUTCOME_VARIANTS: Partial<
  Record<GuidanceOutcomeId, { field: string | null; values: readonly string[] }>
> = {
  // `DecisionInput.decision` (features/procurement/api.ts).
  'approval.decided': {
    field: 'decision',
    values: ['approve', 'reject', 'return', 'partial_approve'],
  },
  // VendorReplyPanel's `respond` takes the answer itself as its variables
  // (`VendorResponseStatus`, features/vendor): `field: null`.
  'vendor.order.answered': {
    field: null,
    values: ['acknowledged', 'query', 'partially_available', 'declined'],
  },
};

const KNOWN: ReadonlySet<string> = new Set(GUIDANCE_OUTCOMES);

export function isGuidanceOutcome(value: unknown): value is GuidanceOutcomeId {
  return typeof value === 'string' && KNOWN.has(value);
}

/** The outcome a mutation declares in its `meta`, or `null` when it declares none. */
export function outcomeOfMeta(meta: unknown): GuidanceOutcomeId | null {
  if (typeof meta !== 'object' || meta === null) return null;

  const value: unknown = (meta as Record<string, unknown>).guidanceOutcome;

  return isGuidanceOutcome(value) ? value : null;
}

/** The allowlisted variant of an outcome, read from the mutation's variables; else nothing. */
export function variantOf(outcome: GuidanceOutcomeId, variables: unknown): string | undefined {
  const rule = OUTCOME_VARIANTS[outcome];

  if (rule === undefined) return undefined;

  let value: unknown = variables;

  if (rule.field !== null) {
    if (typeof variables !== 'object' || variables === null) return undefined;

    value = (variables as Record<string, unknown>)[rule.field];
  }

  return typeof value === 'string' && rule.values.includes(value) ? value : undefined;
}

/** A mutation's state change as a bus phase, or `null` for one the bus does not carry. */
export function phaseOf(actionType: string): OutcomePhase | null {
  if (actionType === 'pending' || actionType === 'success' || actionType === 'error')
    return actionType;

  return null;
}
